import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChatService } from '../../chat.service';
import { avatarHue, avatarOf } from '../../core/avatar';
import { ConversationSummary } from '../../core/hub-types';
import { forwardTargets, MAX_FORWARD_TARGETS } from '../../ui/forward-targets';
import { listRows, typeTag } from '../../ui/conversation-sections';
import { ConversationTone, conversationTone } from '../../ui/conversation-tone';

export interface ForwardPickerData {
  sourceConversationId: string;
  /** a line of what is being forwarded, shown at the top */
  preview: string;
}

/** "Forward to…": a panel in the chat window. Closes with the conversations ticked, in the order they were ticked. */
@Component({
  selector: 'app-chat-forward-picker',
  templateUrl: './forward-picker.component.html',
  styleUrls: ['./forward-picker.component.scss'],
})
export class ForwardPickerComponent {
  readonly max = MAX_FORWARD_TARGETS;
  query = '';
  picked: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: ForwardPickerData,
    readonly ref: MatDialogRef<ForwardPickerComponent, string[]>,
    private readonly chat: ChatService,
  ) {}

  get targets(): ConversationSummary[] {
    const all = Object.values(this.chat.store.snapshot.conversations);
    return forwardTargets(all, this.data.sourceConversationId, this.query, (c) => this.chat.store.titleOf(c));
  }

  /** The targets as the conversation list shows them: one list, newest message first. */
  get rows(): ConversationSummary[] {
    return listRows(this.targets, 'all');
  }

  /** A row's kind, as the list tags it, in its conversation's colour. */
  tag(c: ConversationSummary): string {
    return typeTag(c);
  }

  tone(c: ConversationSummary): ConversationTone {
    return conversationTone(c.type);
  }

  title(c: ConversationSummary): string {
    return this.chat.store.titleOf(c);
  }

  photo(c: ConversationSummary): string | null {
    return this.chat.store.photoUrlOf(c);
  }

  avatar(c: ConversationSummary): { icon: string | null; initials: string | null; hue: number } {
    return avatarOf(c, this.chat.store.snapshot.me?.principalId ?? null) ?? { icon: 'forum', initials: null, hue: avatarHue(c.id) };
  }

  isPicked(c: ConversationSummary): boolean {
    return this.picked.includes(c.id);
  }

  toggle(c: ConversationSummary): void {
    if (this.isPicked(c)) this.picked = this.picked.filter((id) => id !== c.id);
    else if (this.picked.length < this.max) this.picked = [...this.picked, c.id];
  }

  trackById(_i: number, c: ConversationSummary): string {
    return c.id;
  }

  send(): void {
    if (this.picked.length) this.ref.close(this.picked);
  }
}
